// @flow
import React, { useCallback, useContext, useMemo } from 'react';
import moment from 'moment';
import { get } from 'lodash';

import { Datepicker } from 'Components/frameworks/datepicker/Datepicker.jsx';
import { DayWithPrice } from 'Components/widgets/trip-date-picker/day-with-price/DayWithPrice.jsx';
import ReactAppContext from 'Context/ReactAppContext';
import { type PricesByDays } from 'Store/calendar/calendar.helper';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { isSeller as isSellerUtil } from 'Utils/seller/sellerUtils';
import {
  getDisabledDays,
  bestPrice,
  dateToInt,
  lastRoom,
  maxDatepickerDate,
  minDatepickerDate,
  closedDaysFactory,
  flattenPrices,
  type PriceResolver,
} from './TripDatePicker.utils';
import { themes } from './TripDatePicker.themes';

type Props = {
  onSelect?: ({ from: ?Date, to: ?Date }) => mixed,
  navigationByYear?: boolean,
  offsetCalendar?: ?number,
  onDayMouseOver?: (?Date) => mixed,
  openCalendarToDate?: ?number | ?moment$Moment,
  optionsDuration: Array<number>,
  prices?: PricesByDays,
  selectedDays: { from: ?Date, to: ?Date },
  theme?: string,
};

export const TripDatePicker = ({
  navigationByYear,
  offsetCalendar,
  onDayMouseOver,
  onSelect,
  openCalendarToDate,
  optionsDuration,
  prices,
  selectedDays: { from, to },
  theme = 'default',
}: Props) => {
  const appContext = useContext(ReactAppContext);
  const pricesFeatureFlipped = isFeatureFlipped(appContext)('datePicker.displayPrices');

  const onMouseOver = (day: ?Date) => {
    onDayMouseOver && onDayMouseOver(day);
  };

  const pricesIds = Object.keys(prices || {});
  const flattenedPrices = prices ? flattenPrices(prices) : '';
  const hasPrices = pricesIds.length > 0;
  const doDisplayPricesInDatePicker = hasPrices && pricesFeatureFlipped;

  const resolvePrice: PriceResolver = useCallback(
    (dayId: number) => get(prices, `[${dayId}]`, {}),
    [flattenedPrices],
  );
  const isSeller = isSellerUtil(appContext);

  const disabledDays = useMemo(
    () => {
      return getDisabledDays({ from, to, prices, isSeller, optionsDuration, resolvePrice });
    },
    [from, isSeller, optionsDuration.join(), flattenedPrices, to],
  );

  const dayIsClosed = useCallback(
    closedDaysFactory(
      resolvePrice,
      from,
      moment(to)
        .subtract(1, 'day')
        .toDate(),
    ),
    [from, to, flattenedPrices],
  );

  const classColorGreen = 'text-emerald';
  const classColorOrange = 'text-orange';

  const modifiers = {
    [classColorGreen]: hasPrices ? bestPrice(resolvePrice) : [],
    [classColorOrange]: hasPrices ? lastRoom(resolvePrice) : [],
    disabledDays,
    ['pointer-events-none']: (day: Date) => {
      const dayIsSelected =
        from && to && dateToInt(day) >= dateToInt(from) && dateToInt(day) <= dateToInt(to);
      if (hasPrices && dayIsSelected) {
        return dayIsClosed(day);
      }
    },
  };

  const firstDateWithPrices: string = useMemo(
    () => {
      const today = dateToInt(new Date());
      const dayWithPrice = pricesIds.find(
        pricesKey =>
          dateToInt(moment(pricesKey, 'YYYYMMDD').toDate()) > today &&
          prices &&
          prices[pricesKey].available,
      );
      return dayWithPrice || '';
    },
    [flattenedPrices],
  );

  const { thDay } = themes[theme];

  return (
    <Datepicker
      hasRangeMode
      className="pb-0"
      selectedDays={{ from, to }}
      disabledDays={disabledDays}
      customModifiers={modifiers}
      hasNavigationByYear={navigationByYear}
      minDate={minDatepickerDate(offsetCalendar)}
      maxDate={maxDatepickerDate()}
      initialMonth={moment(
        from || openCalendarToDate || firstDateWithPrices || minDatepickerDate(),
      ).toDate()}
      onSelect={onSelect}
      onMouseOver={onMouseOver}
      onFocus={onMouseOver}
      theme={theme}
      {...(doDisplayPricesInDatePicker
        ? {
            // eslint-disable-next-line clubmed/no-fake-component
            renderDay: (day: Date) => (
              <DayWithPrice
                day={day}
                price={resolvePrice(dateToInt(day))}
                shouldDisplayPrice={hasPrices && !from}
                theme={theme}
              />
            ),
            datePickerClassNames: {
              day: thDay,
            },
          }
        : null)}
    />
  );
};
